/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { formatAccrualPeriod } from './lease-detail-derivations';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The backend's requirement names, as the activation refusal lists them.
 * A name not listed here is shown as it came, so a new requirement still
 * reaches the broker even before it has copy of its own.
 */
const REQUIREMENT_KEYS: Record<string, string> = {
  tenant: 'tenant',
  landlords: 'landlords',
  'landlords.share_bps': 'landlordShares',
  'charge_schedule (linha aluguel)': 'rent',
  billing_locale: 'billingLocale',
  warranty_decision: 'warrantyDecision',
};

const ACTIVATE = /^Cannot activate: missing (.+)\.$/;
const CLOSE = /^Cannot close: non-terminal expectation\(s\): (.+)\.$/;
const RESCIND = /^Cannot rescind: expectation\(s\) already received payment: (.+)\.$/;

/** The comma-separated items of a refusal's list. */
function splitList(raw: string): string[] {
  return raw.split(',').map((item) => item.trim()).filter(Boolean);
}

/** A refusal's `YYYY-MM` list as the screen writes months ("out/2026"). */
function periods(raw: string): string {
  return splitList(raw).map((period) => formatAccrualPeriod(period)).join(', ');
}

/** A refusal's requirement names, translated where we have copy for them. */
function requirements(raw: string, t: Translate): string {
  return splitList(raw)
    .map((name) => (REQUIREMENT_KEYS[name] ? t(`refusals.requirements.${REQUIREMENT_KEYS[name]}`) : name))
    .join(', ');
}

/**
 * Turn a lifecycle refusal into words a broker can act on.
 *
 * The backend answers in English and in its own field names ("Cannot
 * activate: missing billing_locale, warranty_decision."), which reached the
 * screen verbatim. The three refusals a broker meets in the normal course of
 * a lease are rewritten; anything else is passed through untouched, since a
 * message we do not recognise is still better than a generic one.
 *
 * @param raw - The backend's `error` string.
 * @param t - The leases translator.
 * @returns The message to show.
 */
export function describeLeaseRefusal(raw: string, t: Translate): string {
  const activate = ACTIVATE.exec(raw);
  if (activate) return t('refusals.activate', { fields: requirements(activate[1], t) });
  const close = CLOSE.exec(raw);
  if (close) return t('refusals.close', { periods: periods(close[1]) });
  const rescind = RESCIND.exec(raw);
  if (rescind) return t('refusals.rescind', { periods: periods(rescind[1]) });
  return raw;
}
